import { FunctionComponent, useEffect, useRef, useState } from "react";
import PageLayout from "@components/core/layouts/PageLayout";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import ProcessSignatureSection from "@views/core/extranet/transactions/process/sections/ProcessSignatureSection";
import ProcessScellementSection from "@views/core/extranet/transactions/process/sections/ProcessScellementSection";
import { useParams } from "react-router-dom";
import {
  requestGetTransactionDetail,
  requestGetTransactionDetailLight,
} from "@state/transactions/TransactionEffects";
import { toastError } from "@utils/helpers/toast-helper";
import { Signatory } from "@type/core/dashboard/dashboard.types";
import { useUnit } from "effector-react";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { authenticationStore } from "@state/auth/AuthStore";
import { REFETCH_TIMEOUT } from "@/utils/Constant";

const ProcessScreen: FunctionComponent = () => {
  const [loadingLightDetails, setLoadingLightDetails] = useState<boolean>(true);
  const transactionId = useParams().transactionId;
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const user = authStore?.user;
  const [transactionLightDetails, setTransactionLightDetails] =
    useState<TransactionDetail>();
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionDetail>();
  const [signatories, setSignatories] = useState<Signatory[]>([]);

  useEffect(() => {
    if (transactionId) {
      if (loadingLightDetails && transactionLightDetails === undefined) {
        void requestGetTransactionDetailLight({ id: transactionId });
      }
      if (
        !loadingLightDetails &&
        transactionLightDetails &&
        transactionLightDetails.direct
      ) {
        if (!signatories || signatories.length === 0) {
          void requestGetTransactionDetail({ id: transactionId });
        }
        if (signatories && signatories.length !== 0) {
          if (
            transactionLightDetails &&
            transactionLightDetails.invitationMode === "parallel"
          ) {
            const directSignatory = signatories.find(
              (s) => s.email === user?.email,
            );
            directSignatory && window.open(directSignatory.url, "_blank");
          } else {
            const firstSignatory = signatories.find((s) => s.rank === 1);
            firstSignatory && window.open(firstSignatory.url, "_blank");
          }
        }
      }
    }
  }, [transactionLightDetails]);

  useEffect(() => {
    return requestGetTransactionDetailLight.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setTransactionLightDetails(result.data.transaction);
        setSignatories(result.data.signatories);
      } else {
        toastError(
          "Erreur lors de la récupération des détails de la transaction",
        );
      }
      setLoadingLightDetails(false);
    });
  });

  useEffect(() => {
    return requestGetTransactionDetail.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setTransactionDetails(result.data.transaction);
        setSignatories(result.data.signatories);
      } else {
        toastError(
          "Erreur lors de la récupération des détails de la transaction",
        );
      }
      setLoadingLightDetails(false);
    });
  });

  const transaction = transactionDetails ?? transactionLightDetails;
  const previousTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (
      transactionId &&
      transaction &&
      transaction.status !== "finished" &&
      transaction.type === "scellement"
    ) {
      if (previousTimeout.current !== null) {
        clearTimeout(previousTimeout.current);
      }

      previousTimeout.current = setTimeout(() => {
        void requestGetTransactionDetail({ id: transactionId });
      }, REFETCH_TIMEOUT);
    }
  }, [transaction]);

  return (
    <PageLayout container isLoading={loadingLightDetails} bodyClassName="mt-0">
      <>
        {transactionLightDetails &&
          transactionLightDetails.type === "signature" && (
            <ProcessSignatureSection
              transaction={transactionDetails ?? transactionLightDetails}
            />
          )}
        {transactionLightDetails &&
          transactionLightDetails.type === "scellement" && (
            <ProcessScellementSection
              transaction={transactionDetails ?? transactionLightDetails}
            />
          )}
      </>
    </PageLayout>
  );
};

export default ProcessScreen;
